import type { AiGenerationDifficulty, AiGenerationExecutionSettings, AiGenerationRequest, AiGenerationScriptSettings, Course, CurriculumMap, DescriptionSubmission, DialogueAttempt, DialogueSpeakerKey, GenerationJob, ImageCandidate, LearningPreference, Lesson, LessonProgress, LearningData, ModuleType, SplitParagraph, TtsSettings, User } from "../../shared/types";

import type { LessonTranslations } from "../../shared/lesson-translation";
import type { BasicsLessonStatus } from "../../shared/basics";
import type { KanaWordStatus } from "../../shared/kana-words";

export interface DialogueSpeakerDraft {
  speakerKey: DialogueSpeakerKey;
  displayName: string;
  audioPresetId: string;
  ttsSettings: TtsSettings;
}

export interface DialogueTurnDraft {
  speakerKey: DialogueSpeakerKey;
  text: string;
}

export function assetUrl(assetId: string): string {
  // Avoid reusing pre-range responses already cached on the learner's device.
  return `/api/assets/${assetId}?delivery=range-v1`;
}

export class ApiClient {
  async getExpressionStatus(): Promise<import("../../shared/kana-words").KanaWordStatus[]> { return this.request("/api/admin/expressions/status"); }
  async prepareExpressionLesson(lessonId: string): Promise<{ id: string }> { return this.request("/api/admin/expressions/lessons", { method: "POST", body: JSON.stringify({ lessonId }) }); }
  private learnerId: string | null;

  constructor(learnerId: string | null = null) {
    this.learnerId = learnerId;
  }

  setLearnerId(learnerId: string | null): void {
    this.learnerId = learnerId;
  }

  async getPinStatus(): Promise<{ unlocked: boolean; pinRequired: boolean }> {
    return this.request<{ unlocked: boolean; pinRequired: boolean }>("/api/pin/status");
  }

  async verifyPin(pin: string): Promise<{ unlocked: boolean }> {
    return this.request<{ unlocked: boolean }>("/api/pin/verify", {
      method: "POST",
      body: JSON.stringify({ pin })
    });
  }

  async getLearningData(): Promise<LearningData> {
    return this.request<LearningData>("/api/learning-data");
  }

  async getMe(): Promise<User> {
    return this.request<User>("/api/me");
  }

  async getLearners(): Promise<User[]> {
    return this.request<User[]>("/api/learners");
  }

  async createLearner(displayName: string): Promise<User> {
    return this.request<User>("/api/admin/learners", {
      method: "POST",
      body: JSON.stringify({ displayName })
    });
  }

  async getCourses(): Promise<Course[]> {
    return this.request<Course[]>("/api/courses");
  }

  async getCurriculumMaps(): Promise<CurriculumMap[]> {
    return this.request<CurriculumMap[]>("/api/curricula");
  }

  async getLessonProgress(): Promise<LessonProgress[]> {
    return this.request<LessonProgress[]>("/api/progress");
  }

  async getComputerProgress(): Promise<{ lessonId: string; userId: string; completedAt: string | null }[]> {
    return this.request("/api/computer/progress");
  }

  async setComputerFinished(lessonId: string, finished: boolean): Promise<{ lessonId: string; userId: string; completedAt: string | null }> {
    return this.request(`/api/computer/progress/${encodeURIComponent(lessonId)}`, { method: "PUT", body: JSON.stringify({ finished }) });
  }

  async getLearningPreference(courseId: string): Promise<LearningPreference> {
    return this.request<LearningPreference>(`/api/preferences/${encodeURIComponent(courseId)}`);
  }

  async updateLearningPreference(courseId: string, furiganaEnabled: boolean): Promise<LearningPreference> {
    return this.request<LearningPreference>(`/api/preferences/${encodeURIComponent(courseId)}`, {
      method: "PUT",
      body: JSON.stringify({ furiganaEnabled })
    });
  }

  async getLessons(moduleType: ModuleType = "reading"): Promise<Lesson[]> {
    return this.request<Lesson[]>(`/api/lessons?moduleType=${encodeURIComponent(moduleType)}`);
  }

  async getLesson(id: string): Promise<Lesson> {
    return this.request<Lesson>(`/api/lessons/${id}`);
  }

  async getLessonTranslations(id: string): Promise<LessonTranslations> {
    return this.request<LessonTranslations>(`/api/lessons/${encodeURIComponent(id)}/translations`, { method: "POST" });
  }

  async setLessonFinished(lessonId: string, finished: boolean): Promise<LessonProgress> {
    return this.request<LessonProgress>(`/api/lessons/${lessonId}/progress`, {
      method: "PATCH",
      body: JSON.stringify({ finished })
    });
  }

  async setKanaItemCompleted(lessonId: string, kanaItemId: string, completed: boolean): Promise<{ lessonId: string; kanaItemId: string; completedAt: string | null }> {
    return this.request(`/api/lessons/${lessonId}/kana-items/${kanaItemId}/progress`, {
      method: "PUT",
      body: JSON.stringify({ completed })
    });
  }

  async createCourse(input: { title: string; domainType: "language" | "subject"; languageCode?: string; subject?: string; level?: string }): Promise<Course> {
    return this.request<Course>("/api/admin/courses", {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  async createCurriculum(input: { courseId: string; title: string; moduleType: ModuleType; description?: string }): Promise<CurriculumMap> {
    return this.request<CurriculumMap>("/api/admin/curricula", {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  async updateCurriculum(input: { curriculumId: string; courseId: string; title: string; moduleType: ModuleType; description?: string }): Promise<CurriculumMap> {
    return this.request<CurriculumMap>(`/api/admin/curricula/${input.curriculumId}`, {
      method: "PATCH",
      body: JSON.stringify({
        courseId: input.courseId,
        title: input.title,
        moduleType: input.moduleType,
        description: input.description
      })
    });
  }

  async deleteCurriculum(curriculumId: string): Promise<{ id: string }> {
    return this.request<{ id: string }>(`/api/admin/curricula/${curriculumId}`, {
      method: "DELETE"
    });
  }

  async createCategory(curriculumId: string, input: { title: string; sortOrder?: number }): Promise<CurriculumMap> {
    return this.request<CurriculumMap>(`/api/admin/curricula/${curriculumId}/categories`, {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  async updateCategory(curriculumId: string, categoryId: string, input: { title?: string; sortOrder?: number }): Promise<CurriculumMap> {
    return this.request<CurriculumMap>(`/api/admin/curricula/${curriculumId}/categories/${categoryId}`, {
      method: "PATCH",
      body: JSON.stringify(input)
    });
  }

  async deleteCategory(curriculumId: string, categoryId: string): Promise<CurriculumMap> {
    return this.request<CurriculumMap>(`/api/admin/curricula/${curriculumId}/categories/${categoryId}`, {
      method: "DELETE"
    });
  }

  async createAiGeneration(input: {
    curriculumId: string;
    plannedLessonId?: string;
    additionalInstructions?: string;
    categoryTitle: string;
    topic: string;
    executionSettings: AiGenerationExecutionSettings;
    targetLength: number;
    difficulty: AiGenerationDifficulty;
    audioPresetId: string;
    ttsSettings: TtsSettings;
    dialogueSpeakers?: DialogueSpeakerDraft[];
  }): Promise<AiGenerationRequest> {
    return this.request<AiGenerationRequest>("/api/admin/ai-generations", {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  async createLocalCliLaunch(): Promise<{ launchUrl: string }> {
    return this.request<{ launchUrl: string }>("/api/admin/local-cli/launch", {
      method: "POST"
    });
  }

  async generateGrammarImages(requestId: string, segmentId: string | null, executionSettings: AiGenerationExecutionSettings): Promise<AiGenerationRequest> {
    return this.request(`/api/admin/ai-generations/${requestId}/grammar-images`, { method: "POST", body: JSON.stringify({ segmentId, executionSettings }) });
  }

  async saveGrammarVisualGuide(lessonId: string, visualGuide: string): Promise<Lesson> {
    return this.request(`/api/admin/lessons/${lessonId}/grammar-visual-guide`, { method: "PUT", body: JSON.stringify({ visualGuide }) });
  }

  async uploadGrammarImage(lessonId: string, segmentId: string, file: File): Promise<Lesson> {
    const body = new FormData();
    body.set("segmentId", segmentId);
    body.set("image", file);
    const headers = new Headers();
    this.addLearnerHeader(headers);
    const response = await fetch(`/api/admin/lessons/${lessonId}/grammar-image`, { method: "POST", body, headers, credentials: "same-origin" });
    const result = await response.json() as Lesson & { error?: string };
    if (!response.ok) throw new Error(result.error ?? "Image upload failed.");
    return result;
  }

  async getAiGenerations(curriculumId: string, categoryTitle: string): Promise<AiGenerationRequest[]> {
    const query = new URLSearchParams({ curriculumId, categoryTitle });
    return this.request<AiGenerationRequest[]>(`/api/admin/ai-generations?${query}`);
  }

  async deleteAiGeneration(requestId: string): Promise<{ id: string }> {
    return this.request<{ id: string }>(`/api/admin/ai-generations/${requestId}`, {
      method: "DELETE"
    });
  }

  async retryAiGeneration(requestId: string, settings: AiGenerationScriptSettings & { executionSettings: AiGenerationExecutionSettings }): Promise<AiGenerationRequest> {
    return this.request<AiGenerationRequest>(`/api/admin/ai-generations/${requestId}/retry`, {
      method: "POST",
      body: JSON.stringify(settings)
    });
  }

  async stopAiGeneration(requestId: string): Promise<AiGenerationRequest> {
    return this.request<AiGenerationRequest>(`/api/admin/ai-generations/${requestId}/stop`, { method: "POST" });
  }

  async regenerateAiGenerationScript(requestId: string, settings: AiGenerationScriptSettings & { executionSettings: AiGenerationExecutionSettings }): Promise<AiGenerationRequest> {
    return this.request<AiGenerationRequest>(`/api/admin/ai-generations/${requestId}/regenerate-script`, {
      method: "POST",
      body: JSON.stringify(settings)
    });
  }

  async generateAiGenerationScript(requestId: string, settings: AiGenerationScriptSettings & { executionSettings: AiGenerationExecutionSettings }): Promise<AiGenerationRequest> {
    return this.request<AiGenerationRequest>(`/api/admin/ai-generations/${requestId}/generate-script`, {
      method: "POST",
      body: JSON.stringify(settings)
    });
  }

  async regenerateAiGenerationImage(requestId: string, candidateId: string, executionSettings: AiGenerationExecutionSettings): Promise<AiGenerationRequest> {
    return this.request<AiGenerationRequest>(`/api/admin/ai-generations/${requestId}/regenerate-image`, {
      method: "POST",
      body: JSON.stringify({ candidateId, executionSettings })
    });
  }

  async selectAiGenerationImage(requestId: string, candidateId: string): Promise<AiGenerationRequest> {
    return this.request<AiGenerationRequest>(`/api/admin/ai-generations/${requestId}/select-image`, {
      method: "POST",
      body: JSON.stringify({ candidateId })
    });
  }

  async previewSegments(sourceText: string) {
    return this.request("/api/admin/lessons/segments/preview", {
      method: "POST",
      body: JSON.stringify({ sourceText })
    });
  }

  async createLesson(input: {
    courseId: string;
    curriculumId?: string;
    categoryTitle?: string;
    sortOrder?: number;
    title: string;
    level?: string;
    sourceText?: string;
    paragraphs?: SplitParagraph[];
    audioPresetId: string;
    ttsSettings?: TtsSettings;
    moduleType?: ModuleType;
    dialogueSpeakers?: DialogueSpeakerDraft[];
    dialogueTurns?: DialogueTurnDraft[];
    selectedWords?: string[];
  }): Promise<Lesson> {
    return this.request<Lesson>("/api/admin/lessons", {
      method: "POST",
      body: JSON.stringify({ ...input, moduleType: input.moduleType ?? "reading" })
    });
  }

  async updateCurriculumLesson(input: {
    curriculumId: string;
    lessonId: string;
    title: string;
    categoryTitle: string;
    sortOrder: number;
  }): Promise<CurriculumMap> {
    return this.request<CurriculumMap>(`/api/admin/curricula/${input.curriculumId}/lessons`, {
      method: "POST",
      body: JSON.stringify({
        lessonId: input.lessonId,
        title: input.title,
        categoryTitle: input.categoryTitle,
        sortOrder: input.sortOrder
      })
    });
  }

  async removeCurriculumLesson(curriculumId: string, lessonId: string): Promise<CurriculumMap> {
    return this.request<CurriculumMap>(`/api/admin/curricula/${curriculumId}/lessons/${lessonId}`, {
      method: "DELETE"
    });
  }

  async updateLesson(lessonId: string, input: { title: string; audioPresetId?: string; ttsSettings?: TtsSettings }): Promise<Lesson> {
    return this.request<Lesson>(`/api/admin/lessons/${lessonId}`, {
      method: "PATCH",
      body: JSON.stringify(input)
    });
  }

  async updateLessonScript(lessonId: string, input: { sourceText: string; paragraphs?: SplitParagraph[] }): Promise<Lesson> {
    return this.request<Lesson>(`/api/admin/lessons/${lessonId}/script`, {
      method: "PATCH",
      body: JSON.stringify(input)
    });
  }

  async updateDialogueLesson(lessonId: string, input: {
    speakers: DialogueSpeakerDraft[];
    turns: DialogueTurnDraft[];
    selectedWords: string[];
  }): Promise<Lesson> {
    return this.request<Lesson>(`/api/admin/lessons/${lessonId}/dialogue`, {
      method: "PUT",
      body: JSON.stringify(input)
    });
  }

  async updateDialogueSpeakerSettings(lessonId: string, speakers: DialogueSpeakerDraft[]): Promise<Lesson> {
    return this.request<Lesson>(`/api/admin/lessons/${lessonId}/dialogue/speakers`, {
      method: "PATCH",
      body: JSON.stringify({ speakers })
    });
  }

  async updateSelectedWords(lessonId: string, selectedWords: string[]): Promise<Lesson> {
    return this.request<Lesson>(`/api/admin/lessons/${lessonId}/selected-words`, {
      method: "PUT",
      body: JSON.stringify({ selectedWords })
    });
  }

  async saveDialogueAttempt(input: {
    lessonId: string;
    segmentId: string;
    recognizedText: string;
    accuracyScore: number;
  }): Promise<DialogueAttempt> {
    return this.request<DialogueAttempt>("/api/dialogue/attempts", {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  async generateAudioPreview(lessonId: string, audioPresetId: string, ttsSettings: TtsSettings) {
    return this.request(`/api/admin/lessons/${lessonId}/audio-preview`, {
      method: "POST",
      body: JSON.stringify({ audioPresetId, ttsSettings })
    });
  }

  async getGenerationJobs(lessonId: string): Promise<GenerationJob[]> {
    return this.request<GenerationJob[]>(`/api/admin/lessons/${lessonId}/generation-jobs`);
  }

  async retryGenerationJob(lessonId: string, jobId: string): Promise<GenerationJob> {
    return this.request<GenerationJob>(`/api/admin/lessons/${lessonId}/generation-jobs/${jobId}/retry`, {
      method: "POST"
    });
  }

  async previewAudioPreset(audioPresetId: string, text: string, ttsSettings: TtsSettings, mode: "voice" | "script" = "voice"): Promise<string> {
    const headers = new Headers({ "Content-Type": "application/json" });
    this.addLearnerHeader(headers);
    const response = await fetch("/api/admin/audio-presets/preview", {
      method: "POST",
      credentials: "same-origin",
      headers,
      body: JSON.stringify({ audioPresetId, text, ttsSettings, mode })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null) as { error?: string } | null;
      throw new Error(body?.error || `Voice preview failed: ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  async generateAudio(lessonId: string, audioPresetId: string, ttsSettings: TtsSettings, selectedWords: string[] = []): Promise<{ jobs: Array<{ id: string; status: string }> }> {
    return this.request<{ jobs: Array<{ id: string; status: string }> }>(`/api/admin/lessons/${lessonId}/generate-audio`, {
      method: "POST",
      body: JSON.stringify({ audioPresetId, ttsSettings, selectedWords })
    });
  }

  async generateKanaAudio(
    lessonId: string,
    audioPresetId: string,
    ttsSettings: TtsSettings,
    replaceExisting = false
  ): Promise<{ jobs: Array<{ id: string; status: string }>; expectedCount: number }> {
    return this.request(`/api/admin/lessons/${lessonId}/kana-audio`, {
      method: "POST",
      body: JSON.stringify({ audioPresetId, ttsSettings, replaceExisting })
    });
  }

  async regenerateAllAudio(
    lessonId: string,
    audioPresetId: string,
    ttsSettings: TtsSettings,
    selectedWords: string[] = []
  ): Promise<{ jobs: Array<{ id: string; status: string }>; deletedAudioCount: number }> {
    return this.request<{ jobs: Array<{ id: string; status: string }>; deletedAudioCount: number }>(`/api/admin/lessons/${lessonId}/generate-audio`, {
      method: "POST",
      body: JSON.stringify({ audioPresetId, ttsSettings, selectedWords, replaceExisting: true })
    });
  }

  async generateWordAudio(
    lessonId: string,
    audioPresetId: string,
    ttsSettings: TtsSettings,
    selectedWords: string[]
  ): Promise<{ jobs: Array<{ id: string; status: string }>; reusedCount: number }> {
    return this.request<{ jobs: Array<{ id: string; status: string }>; reusedCount: number }>(`/api/admin/lessons/${lessonId}/word-audio`, {
      method: "POST",
      body: JSON.stringify({ audioPresetId, ttsSettings, selectedWords })
    });
  }

  async regenerateAudioAsset(lessonAssetId: string, audioPresetId: string, ttsSettings: TtsSettings): Promise<GenerationJob> {
    return this.request<GenerationJob>(`/api/admin/audio-assets/${lessonAssetId}/regenerate`, {
      method: "POST",
      body: JSON.stringify({ audioPresetId, ttsSettings })
    });
  }

  async publishLesson(lessonId: string): Promise<Lesson> {
    return this.request<Lesson>(`/api/admin/lessons/${lessonId}/publish`, {
      method: "POST"
    });
  }

  async createKanaWordLesson(lessonId: string): Promise<{ id: string }> {
    return this.request("/api/admin/kana-words/lessons", { method: "POST", body: JSON.stringify({ lessonId }) });
  }

  async getKanaWordStatus(): Promise<KanaWordStatus[]> {
    return this.request("/api/admin/kana-words/status");
  }

  async createBasicsLesson(planKey: string): Promise<Lesson> {
    return this.request("/api/admin/basics/lessons", { method: "POST", body: JSON.stringify({ planKey }) });
  }

  async getBasicsStatus(): Promise<BasicsLessonStatus[]> {
    return this.request("/api/admin/basics/status");
  }

  async redrawBasicsImages(planKey: string): Promise<{ id: string }> {
    return this.request("/api/admin/basics/images", { method: "POST", body: JSON.stringify({ planKey }) });
  }

  async returnLessonToDraft(lessonId: string): Promise<Lesson> {
    return this.request<Lesson>(`/api/admin/lessons/${lessonId}/draft`, {
      method: "POST"
    });
  }

  async deleteLesson(lessonId: string): Promise<{ id: string }> {
    return this.request<{ id: string }>(`/api/admin/lessons/${lessonId}`, {
      method: "DELETE"
    });
  }

  async searchImages(lessonId: string, query: string): Promise<ImageCandidate[]> {
    return this.request<ImageCandidate[]>(`/api/admin/lessons/${lessonId}/image-candidates`, {
      method: "POST",
      body: JSON.stringify({ query })
    });
  }

  async importImage(lessonId: string, candidateId: string) {
    return this.request(`/api/admin/lessons/${lessonId}/image-assets`, {
      method: "POST",
      body: JSON.stringify({ candidateId })
    });
  }

  async uploadLessonImage(lessonId: string, file: File): Promise<Lesson> {
    const form = new FormData();
    form.set("image", file);
    const headers = new Headers();
    this.addLearnerHeader(headers);
    const response = await fetch(`/api/admin/lessons/${lessonId}/image-upload`, {
      method: "POST",
      credentials: "same-origin",
      headers,
      body: form
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try {
        const body = JSON.parse(text) as { error?: string };
        message = body.error ?? text;
      } catch {
        message = text;
      }
      throw new Error(message || `Image upload failed: ${response.status}`);
    }
    return response.json() as Promise<Lesson>;
  }

  async getDescriptionSubmissions(lessonId: string): Promise<DescriptionSubmission[]> {
    return this.request<DescriptionSubmission[]>(`/api/lessons/${lessonId}/description-submissions`);
  }

  async submitDescription(lessonId: string, responseText: string): Promise<DescriptionSubmission> {
    return this.request<DescriptionSubmission>(`/api/lessons/${lessonId}/description-submissions`, {
      method: "POST",
      body: JSON.stringify({ responseText })
    });
  }

  async sendEvents(events: unknown[]) {
    if (events.length === 0) return;
    await this.request("/api/events/batch", {
      method: "POST",
      body: JSON.stringify({ events })
    });
  }

  async getAnalytics() {
    return this.request("/api/admin/analytics");
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Content-Type", "application/json");
    this.addLearnerHeader(headers);
    const response = await fetch(path, { ...init, credentials: "same-origin", headers });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try {
        const body = JSON.parse(text) as { error?: string };
        message = body.error ?? text;
      } catch {
        message = text;
      }
      throw new Error(message || `Request failed: ${response.status}`);
    }
    return response.json() as Promise<T>;
  }

  private addLearnerHeader(headers: Headers): void {
    if (this.learnerId) headers.set("X-Learner-Id", this.learnerId);
  }
}
